/**
 * openTabsLogic — Pure functions for open tab management
 *
 * Extracted from useOpenTabs so logic can be unit tested without React.
 */

export interface OpenTabsData {
  tabIds: string[]
  activeId: string | null
}

export const EMPTY: OpenTabsData = { tabIds: [], activeId: null }

export function storageKey(workspaceId: string, userEmail?: string) {
  if (userEmail) return `papan-agent-open-tabs-${workspaceId}-${userEmail}`
  return `papan-agent-open-tabs-${workspaceId}`
}

/**
 * The ACTIVE chat is remembered PER BROWSER TAB (sessionStorage: survives a reload,
 * not shared across tabs). The shared localStorage entry (open tab list + active id)
 * is only the default for a brand-new tab. With the active id in localStorage alone,
 * every tab of a project reloaded to whichever chat the last tab had written
 * (zero-downtime test on stagetest2, 2026-09-25). sessionStorage can be missing or
 * throw (private mode, sandboxed frames): then the shared behaviour applies.
 */
function tabActiveKey(workspaceId: string, userEmail?: string) {
  return `${storageKey(workspaceId, userEmail)}:active`
}
function readTabActive(workspaceId: string, userEmail?: string): string | null {
  try { return globalThis.sessionStorage?.getItem(tabActiveKey(workspaceId, userEmail)) ?? null } catch { return null }
}
function writeTabActive(workspaceId: string, activeId: string | null, userEmail?: string) {
  try {
    const ss = globalThis.sessionStorage
    if (!ss) return
    if (activeId) ss.setItem(tabActiveKey(workspaceId, userEmail), activeId)
    else ss.removeItem(tabActiveKey(workspaceId, userEmail))
  } catch { /* unavailable — shared behaviour */ }
}

export function loadFromStorage(workspaceId: string, userEmail?: string): OpenTabsData {
  try {
    const raw = localStorage.getItem(storageKey(workspaceId, userEmail))
    if (raw) {
      const parsed = JSON.parse(raw)
      // Remove stale draft tabs on load — drafts are transient, never persist across reloads
      const tabIds = (Array.isArray(parsed.tabIds) ? parsed.tabIds : []).filter((id: string) => !id.startsWith(DRAFT_PREFIX))
      const shared = parsed.activeId && !parsed.activeId.startsWith(DRAFT_PREFIX) ? parsed.activeId : (tabIds[0] ?? null)
      // This tab's own active chat wins — and is re-added to the list if missing.
      // Every tab writes the shared list from its own in-memory copy, so a tab that
      // never saw this tab's new chat drops it; the list cannot tell "closed
      // elsewhere" from "never known elsewhere", so it must not veto this tab's own
      // chat (manual run B4: a reload showed the newest chat). A deleted chat is
      // still pruned by syncWithSessions against the server's session list.
      const own = readTabActive(workspaceId, userEmail)
      if (own && !own.startsWith(DRAFT_PREFIX)) {
        return { tabIds: tabIds.includes(own) ? tabIds : [...tabIds, own], activeId: own }
      }
      return { tabIds, activeId: shared }
    }
  } catch {
    // corrupted data — start fresh
  }
  return EMPTY
}

export function saveToStorage(workspaceId: string, data: OpenTabsData, userEmail?: string) {
  // Don't persist draft tabs — they're transient and cleaned on reload
  const tabIds = data.tabIds.filter(id => !id.startsWith(DRAFT_PREFIX))
  const activeId = data.activeId && !data.activeId.startsWith(DRAFT_PREFIX) ? data.activeId : (tabIds[0] ?? null)
  localStorage.setItem(storageKey(workspaceId, userEmail), JSON.stringify({ tabIds, activeId }))
  writeTabActive(workspaceId, activeId, userEmail)
}

export function addTab(prev: OpenTabsData, id: string): OpenTabsData | null {
  if (prev.tabIds.includes(id)) return null // no change
  return { ...prev, tabIds: [...prev.tabIds, id] }
}

export function removeTab(prev: OpenTabsData, id: string): { data: OpenTabsData; newActive: string | null } | null {
  const idx = prev.tabIds.indexOf(id)
  if (idx === -1) return null // not found

  const remaining = prev.tabIds.filter(t => t !== id)
  let newActive: string | null

  if (prev.activeId === id) {
    newActive = remaining.length > 0
      ? remaining[Math.min(idx, remaining.length - 1)]
      : null
  } else {
    newActive = prev.activeId
  }

  return { data: { tabIds: remaining, activeId: newActive }, newActive }
}

/**
 * #1402 (owner review, I1) — swap a tab for another IN PLACE: `newId` takes `oldId`'s position in the strip, and is
 * active only if `oldId` was (a draft tab renamed while another tab is active stays where it was, and the user stays
 * where they are). If `newId` is already open (a racing create added it), `oldId` just goes. Null when `oldId` isn't open.
 */
export function replaceTab(prev: OpenTabsData, oldId: string, newId: string): OpenTabsData | null {
  const idx = prev.tabIds.indexOf(oldId)
  if (idx === -1 || oldId === newId) return null
  const tabIds = prev.tabIds.includes(newId)
    ? prev.tabIds.filter(t => t !== oldId)
    : prev.tabIds.map(t => (t === oldId ? newId : t))
  return { tabIds, activeId: prev.activeId === oldId ? newId : prev.activeId }
}

/**
 * Move `movedId` to where `targetId` sits, by ID rather than by index: the
 * strip renders `tabIds` FILTERED to the sessions that still exist, so a
 * display index is not a `tabIds` index whenever one has gone. Returns null
 * when nothing moves, so the caller can skip the write.
 */
export function reorderTabs(prev: OpenTabsData, movedId: string, targetId: string): OpenTabsData | null {
  if (movedId === targetId) return null
  const from = prev.tabIds.indexOf(movedId)
  const to = prev.tabIds.indexOf(targetId)
  if (from === -1 || to === -1) return null
  const next = prev.tabIds.filter(id => id !== movedId)
  next.splice(next.indexOf(targetId) + (to > from ? 1 : 0), 0, movedId)
  return { ...prev, tabIds: next }
}

const DRAFT_PREFIX = 'draft_'

export function syncWithSessions(prev: OpenTabsData, sessionIds: string[]): OpenTabsData {
  const validSet = new Set(sessionIds)
  // Keep draft tabs (client-only, not in sessions list) + valid real tabs
  const pruned = prev.tabIds.filter(id => id.startsWith(DRAFT_PREFIX) || validSet.has(id))

  if (pruned.length === 0 && sessionIds.length > 0) {
    pruned.push(sessionIds[0])
  }

  // Preserve activeId if it's a draft or a valid session
  const activeId = prev.activeId && (prev.activeId.startsWith(DRAFT_PREFIX) || validSet.has(prev.activeId))
    ? prev.activeId
    : pruned[0] ?? null

  return { tabIds: pruned, activeId }
}

/**
 * Queue a conversation to open in the chat, from somewhere that is not the
 * chat (#758 part 2 — the member's Storage page).
 *
 * ── Why it writes storage instead of taking a route ───────────────────
 *
 * There is no `/chat/:sessionId` deep link: which conversations are open
 * is tab state, held here, and the chat reads it on mount. So "open this
 * one" is "add it to the tabs and make it active", then navigate to the
 * chat — which is exactly what the chat's own session list does, through
 * the same functions.
 *
 * It lives in THIS module so the storage format stays owned by the chat.
 * A caller in another feature hand-rolling the same localStorage key is
 * how the two come to disagree about what a tab is.
 *
 * No-op outside a browser, and silent on a storage failure (a private
 * window, blocked site data) — the navigation still happens and the
 * member lands in the chat, which is most of what they asked for.
 */
export function queueSessionTab(workspaceId: string, sessionId: string, userEmail?: string): void {
  try {
    const current = loadFromStorage(workspaceId, userEmail)
    const withTab = addTab(current, sessionId) ?? current
    saveToStorage(workspaceId, { ...withTab, activeId: sessionId }, userEmail)
  } catch { /* best-effort: the navigation below is the part that matters */ }
}
